import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";

// Shared file-drop behaviour for every upload field (ADR-0022). One hook so the
// canonical rules live in a single place:
//   - files-only: only react to OS file drags, never hijack internal item DnD
//   - flicker-free: dragleave bubbles from children → only clear on real leave
//   - reliable clear: any drop resets the state
//   - Esc / abandon resets: see the heartbeat note below
// Visuals (overlay blur / dashed zone) live in FileDropZone; this is behaviour
// only, so a consumer can also wire it onto a bespoke element.
//
// Esc handling: browsers do NOT deliver `keydown` to the page during a native
// OS file drag, so a keydown listener can't catch Esc. Instead we lean on the
// fact that `dragover` fires continuously while a drag is over the element (the
// spec mandates ~every 350ms even when stationary) and stops the instant the
// drag ends for ANY reason — drop, Esc-cancel, or the cursor leaving the
// window. A heartbeat timeout that each dragover refreshes clears the
// affordance shortly after the drag stops, which is what makes Esc "work".

const DRAG_HEARTBEAT_MS = 450;

export interface UseFileDropOptions {
  onFiles: (files: FileList) => void;
  disabled?: boolean;
}

export interface UseFileDrop {
  dragOver: boolean;
  dropProps: {
    onDragOver: (e: DragEvent) => void;
    onDragLeave: (e: DragEvent) => void;
    onDrop: (e: DragEvent) => void;
  };
}

export function useFileDrop({ onFiles, disabled = false }: UseFileDropOptions): UseFileDrop {
  const [dragOver, setDragOver] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stop = useCallback(() => {
    if (timer.current != null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setDragOver(false);
  }, []);

  // Clear any pending heartbeat on unmount.
  useEffect(() => () => {
    if (timer.current != null) clearTimeout(timer.current);
  }, []);

  const onDragOver = useCallback(
    (e: DragEvent) => {
      if (disabled) return;
      // Ignore internal item drag-and-drop (e.g. moving a doc into a folder) —
      // those carry no files and must keep their own drop handlers.
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      setDragOver(true); // React bails out if already true
      // Refresh the heartbeat; if dragover stops coming (drop, Esc, left the
      // window) this fires and clears the affordance.
      if (timer.current != null) clearTimeout(timer.current);
      timer.current = setTimeout(() => setDragOver(false), DRAG_HEARTBEAT_MS);
    },
    [disabled],
  );

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      // dragleave bubbles up from child elements; only clear when the pointer
      // truly leaves this zone (relatedTarget null = left the window).
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      stop();
    },
    [stop],
  );

  const onDrop = useCallback(
    (e: DragEvent) => {
      if (disabled) return;
      stop();
      const files = e.dataTransfer.files;
      if (!files?.length) return;
      e.preventDefault();
      onFiles(files);
    },
    [disabled, onFiles, stop],
  );

  return { dragOver, dropProps: { onDragOver, onDragLeave, onDrop } };
}
